import { connect, FormikValues } from 'formik'
import { useEffect, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { boolean, string } from 'yup'

import Button from '@/components/Button'
import FilterSelect from '@/components/Form/FilterSelect'
import Label from '@/components/Label'
import { FormLabel, Row } from '@/components/StyledComponents'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { VariableInputContainer } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import {
  App,
  Feature,
  RunFeatureStepData,
  StepData,
  TriggerTypes,
  Variable
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getFetchAppQueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'

import CreateRunnableFeatureModal from './CreateRunnableFeatureModal'
import messages from './messages'

//<editor-fold desc="Styled Components">
const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`
const StyledSection = styled.div`
  margin-bottom: 20px;
`
const StyledFilterSelect = styled(FilterSelect)`
  .ui.selection.dropdown .menu > .item {
    padding: 0.5rem 1rem !important;
  }
`
const OptionLabel = styled.label`
  font-size: 14px;
  font-weight: normal;
  color: #3a3a3a;
`
const Description = styled.p`
  margin-top: 2px;
  margin-bottom: 5px;
  margin-right: 5px;
  font-size: 0.8rem;
  word-break: break-word;
  white-space: normal;
  color: #6c6c6c;
`
//</editor-fold>

//<editor-fold desc="Interfaces">
type RunFeatureProps = {
  readOnly?: boolean
}
//</editor-fold>

const getValidationSchema = () => {
  return {
    isAsync: boolean().required('Execution type is a required field'),
    featureId: string().required('Id is a required field'),
    payloadVariable: string().required('Input Variable is a required field')
  }
}

const getInitialValues = (stepData: RunFeatureStepData): StepData => {
  return {
    isAsync: stepData.isAsync,
    featureId: stepData.featureId ?? '',
    payloadVariable: stepData.payloadVariable ?? ''
  }
}

const RunFeatureOperation = ({ formik, readOnly = false }: RunFeatureProps & FormikValues) => {
  const intl = useIntl()
  const { spaceId, appId } = useParams<{ featureId: string; spaceId: string; appId: string }>()

  const featureFlags = useFeatures()
  const agentsFlagOn = featureFlags[WORKSHOP_AGENTS]
  const { getVariablesForStep, variables } = useWorkflowFeature()
  const stepVariables = getVariablesForStep()
  const [showCreateRunnableFeatureModal, setShowCreateRunnableFeatureModal] = useState(false)
  const [runnableFeatures, setRunnableFeatures] = useState<Variable[]>([])
  const appQueryResult = useQuery<App>({
    ...getFetchAppQueryOptions({ spaceId, appId })
  })

  useEffect(() => {
    const runnableFeatures = appQueryResult?.data?.features?.filter(
      (feature: Feature) => feature.type === TriggerTypes.featureTrigger
    )
    const featureVariables = variables.feature?.filter(
      variable =>
        variable.name.startsWith('$.feature') &&
        runnableFeatures?.some((rf: Feature) => rf?.id && variable.name.includes(rf.id))
    )
    if (featureVariables?.length === 1) {
      formik.setFieldValue('stepData.featureId', featureVariables[0].name)
    }
    setRunnableFeatures(featureVariables)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appQueryResult?.data?.features, variables])

  const getExecutionType = (intl: IntlShape) => [
    {
      text: intl.formatMessage(messages.fireAndWait),
      description: intl.formatMessage(messages.fireAndWaitDescription),
      value: false
    },
    {
      text: intl.formatMessage(messages.fireAndForget),
      description: intl.formatMessage(messages.fireAndForgetDescription),
      value: true
    }
  ]

  const getExecutionTypeOptions = (intl: IntlShape) => {
    return getExecutionType(intl).map((executionType: any) => ({
      key: executionType.text,
      text: executionType.text,
      value: executionType?.value,
      content: (
        <div>
          <OptionLabel>{executionType.text}</OptionLabel>
          <Description>{executionType.description}</Description>
        </div>
      )
    }))
  }

  const onSelectVariable = (variable: Variable, formikName: string) => {
    formik.setFieldValue(formikName, variable.name)
  }

  return stepVariables?.length > 0 ? (
    runnableFeatures?.length > 0 ? (
      <>
        <StyledSection>
          <StyledRow>
            <Label required>{intl.formatMessage(messages.runType)}</Label>
          </StyledRow>
          <StyledFilterSelect
            onBlur={() => {}}
            disabled={readOnly}
            name={`stepData.isAsync`}
            placeholder={intl.formatMessage(messages.selectRunType)}
            options={getExecutionTypeOptions(intl)}
            selection={true}
            showValidationMessage={true}
            required
            className="fill-space"
          />
        </StyledSection>

        <StyledSection>
          <StyledRow>
            <Label required>{agentsFlagOn ? 'Capability' : 'Feature'} To Run</Label>
          </StyledRow>
          <VariableInputContainer>
            <VariableAutoComplete
              readOnly={readOnly}
              name="stepData.featureId"
              variables={[runnableFeatures]}
              placeholder={`${agentsFlagOn ? 'Capability' : 'Feature'} Id`}
              // @ts-expect-error
              onSelectOption={onSelectVariable}
              showValidationMessage={true}
              required
            />
          </VariableInputContainer>
        </StyledSection>
        <StyledSection>
          <StyledRow>
            <Label required>{agentsFlagOn ? 'Capability' : 'Feature'} Data</Label>
          </StyledRow>
          <VariableInputContainer>
            <VariableAutoComplete
              readOnly={readOnly}
              name="stepData.payloadVariable"
              variables={stepVariables}
              placeholder={intl.formatMessage(messages.variableInput)}
              // @ts-expect-error
              onSelectOption={onSelectVariable}
              showValidationMessage={true}
              required
            />
          </VariableInputContainer>
        </StyledSection>
      </>
    ) : (
      <div className="bg-white px-4 py-12">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              fill="#cbd5e0"
              d="M4.875 4C3.83947 4 3 4.83947 3 5.875V9.625C3 10.6605 3.83947 11.5 4.875 11.5H10.25V11.7222C10.25 12.9649 11.2574 13.9722 12.5 13.9722C12.9142 13.9722 13.25 14.308 13.25 14.7222V15H7.375C6.33947 15 5.5 15.8395 5.5 16.875V19.125C5.5 20.1605 6.33947 21 7.375 21H19.375C20.4105 21 21.25 20.1605 21.25 19.125V16.875C21.25 15.8395 20.4105 15 19.375 15H14.75V14.7222C14.75 13.4796 13.7426 12.4722 12.5 12.4722C12.0858 12.4722 11.75 12.1364 11.75 11.7222V11.5H17.625C18.6605 11.5 19.5 10.6605 19.5 9.625V5.875C19.5 4.83947 18.6605 4 17.625 4H4.875ZM4.5 5.875C4.5 5.66789 4.66789 5.5 4.875 5.5H17.625C17.8321 5.5 18 5.66789 18 5.875V9.625C18 9.83211 17.8321 10 17.625 10H4.875C4.66789 10 4.5 9.83211 4.5 9.625V5.875ZM7 16.875C7 16.6679 7.16789 16.5 7.375 16.5H19.375C19.5821 16.5 19.75 16.6679 19.75 16.875V19.125C19.75 19.3321 19.5821 19.5 19.375 19.5H7.375C7.16789 19.5 7 19.3321 7 19.125V16.875Z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            There are currently no {agentsFlagOn ? 'capabilities' : 'features'} in this app with a{' '}
            {agentsFlagOn ? 'capability' : 'feature'} trigger.
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new {agentsFlagOn ? 'capability' : 'feature'}.
          </p>
          <div className="mt-4 flex justify-center">
            <Button color="secondary" onClick={() => setShowCreateRunnableFeatureModal(true)}>
              Create {agentsFlagOn ? 'Capability' : 'Feature'}
            </Button>
            <CreateRunnableFeatureModal
              isOpen={showCreateRunnableFeatureModal}
              onClose={() => setShowCreateRunnableFeatureModal(false)}
              agentsFlagOn={!!agentsFlagOn}
            ></CreateRunnableFeatureModal>
          </div>
        </div>
      </div>
    )
  ) : (
    <StyledRow>
      <FormLabel>
        Please add a step before this run {agentsFlagOn ? 'capability' : 'feature'} operation that outputs variables
      </FormLabel>
    </StyledRow>
  )
}

export default connect(RunFeatureOperation)

export { getInitialValues, getValidationSchema }
